/*
 * Estado del header segun la posicion de scroll.
 *
 * Publica **una** clase, `header--scrolled`, en `.shopify-section-header`: esta
 * puesta cuando la pagina dejo el top y ausente cuando esta arriba de todo. De
 * ahi cuelgan dos cosas:
 *
 *   1. El ancho del logo (`ig-header.css` lo transiciona entre
 *      `--header-logo-width` y `--header-logo-width-scrolled`).
 *   2. El modo **solido**: con `data-solid-on-scroll="true"` el header deja de
 *      adaptarse al fondo de cada seccion y se queda con el esquema claro.
 *
 * Por que hace falta JS: la clase sticky de Madrid se decide con
 * `window.pageYOffset`, y en el home con el scroll slider
 * (`settings.home_page_slider`) **no hay scroll de ventana** — cada seccion es
 * un slide de un Swiper vertical —, asi que nunca se agrega. Los tres origenes
 * de scroll a mirar son: la ventana, el Swiper del home y el
 * `.slider-scroll-pane` (el tramo que un marcador dejo con scroll nativo).
 *
 * Y por que el modo solido tiene que reescribir clases en vez de resolverse en
 * CSS: en el header, `color-background-2` **no** es "esquema oscuro", es
 * `background-color: transparent` + swap al logo `--overlay` (`base.css:3998`
 * y `:4010-4021`). Sobre una seccion blanca eso deja el header invisible.
 * Encima hay **seis** actores escribiendo esas mismas clases sin coordinarse
 * (las reglas `:has()` del base, `global.js:initHeaderOverlay`, el estado
 * inicial y el hover de `header.js`, `slider-demo.js:changeNavStyle()` y este
 * archivo), asi que el resultado dependia del orden en que llegaran.
 *
 * La regla, en una linea:
 *
 *     transparente  <=>  atTop && overlayHere && !forcedSolid
 *
 *   - `atTop`       lo decide `leftTheTop()` (slide 0 con el slider; el tope de
 *                   la pagina si no).
 *   - `overlayHere` es un **hecho del DOM**: la primera seccion de `main` tiene
 *     `.section--has-overlay`. Es la misma prueba que hace
 *     `global.js:initHeaderOverlay`, que se declara autoridad del tema base.
 *   - `forcedSolid` es hover/foco sobre el header, o un mega-menu abierto.
 *
 * Antes esto se **aprendia** mirando las clases del propio header, y se rompia
 * de dos maneras: `changeNavStyle()` borra las dos clases cuando el fondo del
 * slide es transparente (su `isLightColor` devuelve `null`), con lo que nunca
 * se aprendia que correspondia transparencia; y al reponerla en cada update se
 * deshacia el hover de `header.js` en el acto.
 *
 * Ojo con un supuesto viejo que resulto falso: el comentario anterior decia que
 * no se podia mirar el DOM "porque el banner de la PLP tambien lleva
 * `.section--has-overlay`". No la lleva — `collection-banner-plp.liquid` no la
 * emite. Las unicas secciones que la emiten son `slideshow`, `media-banner`,
 * `video-banner` y `main-product`.
 *
 * El `MutationObserver` de abajo hace determinista el resultado: escriba quien
 * escriba, este archivo repone el estado que corresponde. Las escrituras son
 * idempotentes (`classList.toggle` con booleano no genera mutacion si el estado
 * ya es el correcto), asi que no hay bucle.
 *
 * Efecto lateral de la regla nueva: `header.js` y este archivo ahora
 * **coinciden** —hover quita la transparencia, salir la repone arriba de todo—,
 * asi que no hace falta tocar ese archivo, que es base Madrid.
 */
(function () {
  const CLASS = 'header--scrolled';
  /*
   * Segunda clase, esta en el `<body>`: el chrome de la home (barra de anuncios
   * + header) se retira al avanzar de bloque y vuelve al retroceder o al subir
   * el scroll. Vive en este archivo y no en uno propio porque necesita
   * exactamente el mismo enganche al Swiper que ya esta resuelto abajo.
   *
   * Gate: `home-chrome-autohide`, que `layout/theme.liquid` imprime solo en la
   * home con el slider activo y con `settings.home_hide_chrome` en on.
   */
  const HIDE_CLASS = 'home-chrome-hidden';
  const AUTOHIDE_CLASS = 'home-chrome-autohide';
  // Un gesto tiene que ser intencional: un trackpad manda deltas de 1-2px al
  // apoyar los dedos y no queremos que eso mueva el chrome.
  const GESTURE_THRESHOLD = 6;
  // Las unicas clases cuyo cambio obliga a reponer el estado.
  const SCHEME_CLASSES = ['color-background-1', 'color-background-2', 'have-background'];
  // Adentro de un pane basta con que se haya movido: el umbral solo evita que
  // un rebote de scroll de 1px prenda y apague la clase.
  const PANE_THRESHOLD = 8;
  /*
   * Franja del borde superior que trae el chrome de vuelta con el puntero.
   *
   * 40 y no 12: una franja fina es un blanco imposible de acertar —el gesto
   * humano es "llevar el mouse arriba", no apuntar a un sliver—, y ademas la
   * primera version pedia *cruzar* hacia la franja, asi que un puntero que
   * entraba a la ventana desde la barra del navegador (o sea, ya adentro de la
   * franja) no disparaba nunca. Ver `onPointerMove()`.
   *
   * El umbral para volver a esconderlo es el alto del chrome entero (~100), o
   * sea que hay histeresis: se entra por 40 y se sale por 100.
   */
  const HOVER_REVEAL_ZONE = 40;
  /*
   * Gracia antes de volver a esconderlo. Sin esto, rozar el borde superior
   * camino a otra cosa dejaba el chrome entrando y saliendo; y el propio pase
   * de entrada (550ms) mueve contenido bajo el cursor, lo que genera
   * `pointermove` sinteticos del navegador.
   */
  const HOVER_HIDE_DELAY = 450;

  let frame = null;
  let boundSwiper = null;
  let classObserver = null;
  let applying = false;
  /*
   * El foco es la unica entrada de `forcedSolid` que lleva estado, y hay una
   * razon: durante el despacho de `focusout` el `document.activeElement` es
   * transitoriamente `<body>`, asi que `:focus-within` se lee `false` por un
   * instante. Eso es exactamente el parpadeo al tabular. El hover, en cambio,
   * se lee del DOM — ver `forcedSolid()`.
   */
  let focusInside = false;
  // A que nodo esta enganchado `classObserver`: el editor puede reemplazar el
  // header y hay que re-instalarlo.
  let observedHeader = null;
  /*
   * `true` mientras el chrome este a la vista **solo** porque el puntero lo
   * trajo. Es lo que distingue "lo mostro el hover" (y por lo tanto se va al
   * alejarse) de "lo mostro el scroll o el primer bloque" (y se queda). Por eso
   * `onGesture()` lo apaga: un gesto siempre se lleva la autoridad del estado.
   */
  let hoverRevealed = false;
  /*
   * Candado anti ping-pong: cuando un gesto esconde el chrome con el puntero ya
   * apoyado en la franja, la revelacion queda inhibida hasta que el puntero
   * salga de ella. Si no, esconder por scroll y mover un pixel el mouse lo
   * traia de vuelta en el acto.
   */
  let hoverSuppressed = false;
  let hideTimer = null;
  // Ultimo `clientY` conocido del puntero. Lo necesita `setChrome()` para saber
  // si el chrome se esta escondiendo con el puntero encima de la franja.
  let pointerY = null;
  const DESKTOP_MIN = 1100;

  function getHeader() {
    return document.querySelector('.shopify-section-header');
  }

  function solidMode() {
    const wrapper = document.querySelector('.header-wrapper[data-solid-on-scroll]');
    return wrapper ? wrapper.dataset.solidOnScroll === 'true' : false;
  }

  /* El hover y el mega-menu vuelven solido el header transparente. Es opt-out
     por si alguna vez se quiere la transparencia quieta. */
  function solidOnHover() {
    const wrapper = document.querySelector('.header-wrapper[data-solid-on-hover]');
    return wrapper ? wrapper.dataset.solidOnHover === 'true' : true;
  }

  /*
   * Que seccion esta debajo del header ahora mismo.
   *
   * Con el slider no alcanza con mirar el DOM de `main`: el orden no dice que
   * se esta viendo, eso lo dice `activeIndex`. Y si el slide activo es un
   * `.slider-scroll-pane` —un tramo con varias secciones y scroll nativo— hay
   * que resolverlo por geometria, igual que `paneBackgroundAt()` en
   * `slider-demo.js`.
   */
  function topSection(header) {
    const swiper = getSliderSwiper();
    if (swiper && swiper.slides) {
      const slide = swiper.slides[swiper.activeIndex];
      if (!slide) return null;
      if (!slide.classList.contains('slider-scroll-pane')) return slide;

      const y = header.getBoundingClientRect().bottom + 1;
      const children = slide.children;
      for (let i = 0; i < children.length; i += 1) {
        const rect = children[i].getBoundingClientRect();
        if (rect.top <= y && rect.bottom > y) return children[i];
      }
      return slide.firstElementChild;
    }

    // Sin slider se usa el mismo selector que `header.js`, para heredar el
    // `.not-margin` que escribe `global.js:initHeaderOverlay` cuando el overlay
    // no corresponde (el header no es la ultima seccion del header-group).
    return document.querySelector('main .shopify-section:first-child:not(.not-margin)');
  }

  /*
   * "El header va transparente aca" es un **hecho del DOM**, no algo que haya
   * que aprender: lo dice la seccion de arriba. Misma prueba que
   * `global.js:initHeaderOverlay`, incluido el descarte del header de articulo.
   *
   * El chequeo va acotado a la seccion y nunca con
   * `header.classList.contains(...)`: `main-product.liquid` le copia
   * `section--has-overlay` **al header mismo**, y leerla de ahi es justo el bug
   * que dejo muerta una rama entera de `header.js`.
   */
  function overlayHere(header) {
    const section = topSection(header);
    if (!section) return false;
    if (section.classList.contains('not-margin')) return false;
    return !!section.querySelector('.section--has-overlay:not(.article-template__header)');
  }

  /* En tactil `:hover` queda pegado despues de un tap: sin esta guarda el
     header se quedaria solido para siempre sobre el slideshow mobile. */
  function hoverCapable() {
    return (
      window.matchMedia('(hover: hover)').matches && window.innerWidth >= DESKTOP_MIN
    );
  }

  /*
   * Tres entradas, ninguna con contabilidad de hover.
   *
   * El hover se lee del DOM (`:hover`) y **no** de un flag propio, y esa es la
   * pieza que arregla el bug: el navegador setea `:hover` *antes* de despachar
   * `mouseenter`, asi que cuando corre el callback del MutationObserver
   * —microtask disparada por la escritura de `header.js` desde su
   * ResizeObserver— el flag ya es `true`. Con una bandera propia el observer
   * podia llegar primero, leerla vieja y reponer la transparencia, que es
   * exactamente lo que deshacia el hover.
   *
   * El mega-menu se lee de `.burger-menu--open`. Su panel **es descendiente del
   * header** (`mega-menu` se renderiza dentro del `<nav>`), asi que `:hover`
   * solo ya alcanzaria; se chequea explicito para no depender de eso.
   */
  function forcedSolid(header) {
    if (!solidOnHover()) return false;
    if (document.querySelector('.burger-menu--open')) return true;
    if (focusInside || header.matches(':focus-within')) return true;
    return hoverCapable() && header.matches(':hover');
  }

  function getSliderSwiper() {
    const demo = document.querySelector('.slider-demo');
    return demo && demo.swiper ? demo.swiper : null;
  }

  /* ---------- Chrome de la home ---------- */

  function autohideOn() {
    return document.body && document.body.classList.contains(AUTOHIDE_CLASS);
  }

  /*
   * El desplazamiento del chrome se publica como **un** valor medido, no como un
   * `calc()` de las variables del tema. Dos razones:
   *
   *   1. `header.js` mantiene `--ann-height` con un IntersectionObserver sobre la
   *      barra, asi que el propio transform la pone en 0 y el calculo se
   *      realimenta.
   *   2. El `calc(-1 * (var(--header-height) + var(--announcement-height)))`
   *      resolvia invalido sobre el header — medido con
   *      `CSS.getMatchedStylesForNode`: la regla matchea y es la unica que
   *      declara `transform`, y aun asi el computado quedaba en la identidad.
   *
   * `offsetHeight` ignora los transforms, asi que medir no se contamina con el
   * estado que estamos aplicando. Mismo criterio que `--pgm-band-h`.
   */
  function publishChromeHeight() {
    if (!autohideOn()) return;
    const ann = document.querySelector('.section-announcement');
    const header = getHeader();
    const total = (ann ? ann.offsetHeight : 0) + (header ? header.offsetHeight : 0);
    if (total > 0) document.body.style.setProperty('--ig-chrome-h', total + 'px');
  }

  /* Un drawer, el carrito o el buscador bloquean el scroll del body mientras
     estan abiertos: el gesto es de ellos, y ademas el header tiene que quedar
     alcanzable. Mismo chequeo que `home-scroll-snap.js`. */
  function isLocked() {
    return (
      getComputedStyle(document.body).overflowY === 'hidden' ||
      getComputedStyle(document.documentElement).overflowY === 'hidden'
    );
  }

  /*
   * Arriba de todo el chrome siempre se muestra, y "arriba de todo" depende de
   * que motor mueve la home:
   *
   *   - Con el slider (desktop, o mobile si `home_page_mobile_slider` esta
   *     puesto) es el primer bloque: ahi el header transparente sobre el
   *     slideshow es el diseno, y ademas es el unico slide donde esconderlo no
   *     tendria de donde volver.
   *   - Sin slider (el mobile de esta tienda) la home scrollea normal, asi que
   *     es el tope de la pagina. Y hay que esperar al umbral del header: por
   *     debajo de eso el header todavia esta en el flujo, no `fixed`, y
   *     esconderlo dejaria un hueco arriba.
   */
  function chromeMustShow() {
    const swiper = getSliderSwiper();
    if (swiper) return swiper.activeIndex === 0;

    const header = getHeader();
    const top = window.pageYOffset || document.documentElement.scrollTop || 0;
    return !header || top <= header.offsetHeight;
  }

  function setChrome(hide) {
    if (!autohideOn()) return;
    // Se remide con el chrome a la vista: ahi los dos elementos tienen su alto
    // real y el logo todavia no se achico.
    if (!document.body.classList.contains(HIDE_CLASS)) publishChromeHeight();
    if (hide && (chromeMustShow() || isLocked())) hide = false;
    document.body.classList.toggle(HIDE_CLASS, hide);

    /*
     * El candado del hover se arma aca y no en `onGesture()`: al chrome lo
     * esconden **dos** caminos —el gesto y el `slideChange` del Swiper—, y
     * ponerlo en uno solo dejaba el otro sin candado. Medido: escondiendo con
     * `slideNext()` y el puntero apoyado en la franja, el chrome volvia solo.
     *
     * Va despues del guard, asi que un `hide` que `chromeMustShow()` rechazo no
     * arma nada.
     */
    if (hide) {
      cancelHide();
      hoverRevealed = false;
      hoverSuppressed = pointerY !== null && pointerY <= HOVER_REVEAL_ZONE;
    }
  }

  /* Un gesto hacia arriba siempre muestra; uno hacia abajo esconde. Cubre a la
     vez el cambio de slide y el scroll nativo de un `.slider-scroll-pane`, que
     es el otro lugar donde se scrollea en la home.

     El gesto **se lleva la autoridad**: apaga `hoverRevealed`, asi que un chrome
     que el visitante trajo scrolleando hacia arriba no se va despues solo por
     mover el mouse. */
  function onGesture(deltaY) {
    if (!autohideOn()) return;
    if (deltaY <= -GESTURE_THRESHOLD) {
      cancelHide();
      hoverRevealed = false;
      setChrome(false);
    } else if (deltaY >= GESTURE_THRESHOLD) {
      // Las banderas del hover las arma `setChrome()`.
      setChrome(true);
    }
  }

  /* ---------- El puntero en el borde superior trae el chrome ---------- */

  /* Gateado por `settings.home_chrome_hover_reveal`, que `layout/theme.liquid`
     imprime como data-attribute del `<body>` (el asset es estatico). La guarda
     de plataforma es la misma que la del header solido: en tactil `:hover`
     queda pegado despues de un tap y el chrome no se iria nunca. */
  function hoverRevealOn() {
    if (!document.body || document.body.dataset.chromeHoverReveal === 'false') return false;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  /* `offsetHeight` ignora los transforms, asi que mide igual con el chrome
     afuera — mismo criterio que `publishChromeHeight()`. */
  function chromeHeight() {
    const ann = document.querySelector('.section-announcement');
    const header = getHeader();
    return (ann ? ann.offsetHeight : 0) + (header ? header.offsetHeight : 0);
  }

  /* Mientras el foco o el mega-menu esten adentro del header, el chrome se
     queda: el panel del mega-menu es descendiente del header y baja mucho mas
     abajo del alto del chrome, asi que sin esto se escondia con el menu
     abierto. */
  function chromeBusy() {
    const header = getHeader();
    if (document.querySelector('.burger-menu--open')) return true;
    return !!header && (focusInside || header.matches(':focus-within') || header.matches(':hover'));
  }

  function cancelHide() {
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
  }

  function onPointerMove(event) {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (!autohideOn() || !hoverRevealOn()) return;

    const y = event.clientY;
    pointerY = y;
    // Salir de la franja libera el candado: de ahi en mas el puntero puede
    // volver a traer el chrome.
    if (y > HOVER_REVEAL_ZONE) hoverSuppressed = false;

    if (document.body.classList.contains(HIDE_CLASS)) {
      /*
       * Basta con **estar** en la franja; no se pide cruzarla. El cruce parecia
       * la forma prolija de evitar el ping-pong con el gesto, pero deja afuera
       * el caso mas comun: el puntero que baja desde la barra del navegador
       * entra a la pagina ya adentro de la franja, sin un evento previo por
       * debajo, y entonces no disparaba. Del ping-pong se ocupa
       * `hoverSuppressed`, que es el candado preciso.
       */
      if (y <= HOVER_REVEAL_ZONE && !hoverSuppressed) {
        cancelHide();
        hoverRevealed = true;
        setChrome(false);
      }
      return;
    }

    if (!hoverRevealed) return;

    if (y <= chromeHeight() || chromeBusy()) {
      cancelHide();
      return;
    }

    // Con gracia: rozar el borde superior camino a otra cosa no tiene que dejar
    // el chrome parpadeando.
    if (hideTimer) return;
    hideTimer = setTimeout(function () {
      hideTimer = null;
      if (!hoverRevealed || chromeBusy()) return;
      hoverRevealed = false;
      setChrome(true);
    }, HOVER_HIDE_DELAY);
  }

  function leftTheTop(header) {
    // Madrid decide su header sticky con el alto del propio header; se usa el
    // mismo umbral para que el logo y el fondo del header cambien juntos.
    const top = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (top > header.offsetHeight) return true;

    const swiper = getSliderSwiper();
    if (swiper) {
      if (swiper.activeIndex > 0) return true;
      const slide = swiper.slides ? swiper.slides[swiper.activeIndex] : null;
      if (slide && slide.scrollTop > PANE_THRESHOLD) return true;
    }

    return false;
  }

  /*
   * El slider no dispara `scroll` al cambiar de slide, asi que hay que
   * escuchar a la instancia. Se re-engancha sola: `slider-demo.js` destruye y
   * vuelve a crear el Swiper en cada resize que cruza los 1100px y en
   * `shopify:section:load`.
   */
  function bindSwiper() {
    const swiper = getSliderSwiper();
    if (!swiper || swiper === boundSwiper) return;

    boundSwiper = swiper;
    swiper.on('slideChange', function () {
      // `previousIndex` da el sentido sin tener que llevar el estado a mano.
      setChrome(swiper.activeIndex > swiper.previousIndex);
      schedule();
    });
    swiper.on('slideChangeTransitionEnd', schedule);
    swiper.on('destroy', function () {
      boundSwiper = null;
    });
  }

  function applyScheme(header, scrolled) {
    if (!solidMode()) return;

    /*
     * La regla entera. Transparente solo arriba de todo, solo si esta pagina
     * tiene overlay, y solo si nada lo esta forzando a solido.
     */
    const wantOverlay = !scrolled && overlayHere(header) && !forcedSolid(header);

    /*
     * Una sola escritura, y **solo** sobre `color-background-2`: solido =
     * ausencia de esa clase, porque el fondo por defecto del header ya es
     * `rgb(var(--color-background))` con su borde inferior.
     *
     * No se toca `color-background-1`: `StickyHeader.onScroll` (en
     * `header.liquid`) hace `if (header.classList.contains(
     * headerBackgroundColorScheme)) header.classList.add('color-background-2')`
     * al volver al top, y el esquema configurado del header **es**
     * `color-background-1`. Agregarla activaba esa rama y la PLP volvia al top
     * transparente, o sea invisible.
     *
     * `toggle` con booleano es idempotente: si el estado ya es el correcto no
     * hay mutacion, asi que el observer no se dispara solo.
     */
    header.classList.toggle('color-background-2', wantOverlay);
  }

  function update() {
    frame = null;
    const header = getHeader();
    if (!header) return;
    if (applying) return;

    applying = true;
    bindSwiper();
    const scrolled = leftTheTop(header);
    header.classList.toggle(CLASS, scrolled);
    applyScheme(header, scrolled);
    applying = false;

    // Se re-instala si el editor reemplazo el nodo del header: con un
    // `if (!classObserver)` pelado el observer quedaba colgado del nodo viejo.
    if (observedHeader && observedHeader !== header && classObserver) {
      classObserver.disconnect();
      classObserver = null;
    }

    if (!classObserver && solidMode()) {
      /*
       * Cualquier otro que escriba las clases de esquema —el hover de
       * `header.js`, el `changeNavStyle()` del slider— vuelve a pasar por aca.
       *
       * La correccion va **sincrona**, no en un `requestAnimationFrame`: el
       * callback de un MutationObserver es una microtask, o sea que corre antes
       * del paint y el frame se pinta ya con el estado correcto. Con el rAF
       * llegaba un frame tarde y se veia el parpadeo — medido, ~17ms con el
       * logo normal en lugar del `--overlay` arriba del slideshow.
       *
       * `header.js` escribe desde el callback de un `ResizeObserver`, que corre
       * **despues** del rAF del mismo frame: de ahi que el rAF nunca podia
       * ganarle.
       */
      classObserver = new MutationObserver(function (records) {
        if (applying) return;

        // Solo importan las clases de esquema. El resto (`entering-section`,
        // `animate`, la sticky de Madrid) cambian en cada scroll y no hay que
        // forzar layout por ellas.
        const relevante = records.some(function (record) {
          const antes = (record.oldValue || '').split(/\s+/);
          const ahora = Array.prototype.slice.call(record.target.classList);
          return SCHEME_CLASSES.some(function (cls) {
            return antes.indexOf(cls) !== -1 !== (ahora.indexOf(cls) !== -1);
          });
        });

        if (relevante) update();
      });
      classObserver.observe(header, {
        attributes: true,
        attributeFilter: ['class'],
        attributeOldValue: true,
      });
      observedHeader = header;
    }
  }

  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(update);
  }

  // `capture: true` es lo que hace que lleguen los `scroll` de los scrollers de
  // adentro —el pane del home, sin ir mas lejos—: `scroll` no burbujea, pero la
  // fase de captura si baja por el arbol.
  document.addEventListener('scroll', schedule, { capture: true, passive: true });

  document.addEventListener('wheel', function (event) {
    onGesture(event.deltaY);
  }, { passive: true });

  /* `pointermove` y no `mouseenter` sobre el header: con el chrome escondido el
     header esta fuera de la pantalla, asi que no hay nada sobre lo que entrar —
     la franja del borde superior es la unica senal posible. */
  document.addEventListener('pointermove', onPointerMove, { passive: true });

  /*
   * El puntero se fue de la ventana. **No se esconde nada**: la unica forma de
   * salir por arriba es pasando por la franja, o sea que esconder ahi le quita
   * el chrome justo al visitante que lo estaba yendo a buscar —y despues, con
   * el puntero fuera, no hay evento con el que recuperarlo—. Se cancela la
   * gracia pendiente y se deja el chrome como esta; lo esconde el proximo
   * gesto.
   */
  document.addEventListener('pointerleave', cancelHide);

  /* Sin slider el sentido sale del scroll de la ventana y no de la rueda: asi
     cubre tambien la barra de scroll, el teclado y la inercia del touch. */
  let lastScrollY = window.pageYOffset || 0;
  window.addEventListener('scroll', function () {
    if (!autohideOn() || getSliderSwiper()) return;
    const y = window.pageYOffset || document.documentElement.scrollTop || 0;
    onGesture(y - lastScrollY);
    lastScrollY = y;
  }, { passive: true });

  /* En tactil el sentido sale de comparar contra el punto donde arranco el
     dedo: arrastrar hacia abajo (`clientY` creciendo) es ir hacia atras. */
  let touchY = null;
  document.addEventListener('touchstart', function (event) {
    touchY = event.touches && event.touches[0] ? event.touches[0].clientY : null;
  }, { passive: true });

  document.addEventListener('touchmove', function (event) {
    if (touchY === null || !event.touches || !event.touches[0]) return;
    const y = event.touches[0].clientY;
    onGesture(touchY - y);
    touchY = y;
  }, { passive: true });

  /* Con el chrome afuera sus links siguen en el orden de tabulacion: si el
     foco entra ahi, hay que traerlo de vuelta a la pantalla. */
  document.addEventListener('focusin', function (event) {
    if (!autohideOn()) return;
    const target = event.target;
    if (target && target.closest && target.closest('.shopify-section-header, .section-announcement')) {
      setChrome(false);
    }
  });

  /*
   * Hover y foco sobre el header. Van sobre el elemento y no delegados en
   * `document` porque `mouseenter`/`mouseleave` no burbujean.
   *
   * Se corrige **sincrono** (`update()` directo, no `schedule()`): el rAF llega
   * un frame tarde y se ve el parpadeo, por el mismo motivo que ya documenta el
   * MutationObserver de abajo — `header.js` escribe desde el callback de un
   * `ResizeObserver`, que corre despues del rAF del mismo frame.
   */
  function bindHeaderHover() {
    const header = getHeader();
    if (!header || header.dataset.igHoverBound === 'true') return;
    header.dataset.igHoverBound = 'true';

    // El hover no guarda estado: `forcedSolid()` lee `:hover` del DOM. Estos
    // dos solo despiertan al arbitro.
    header.addEventListener('mouseenter', update);
    header.addEventListener('mouseleave', update);

    header.addEventListener('focusin', function () {
      focusInside = true;
      update();
    });

    // `relatedTarget` es a donde va el foco: si sigue adentro del header no hay
    // que apagar nada. Sin este chequeo, tabular de un link al siguiente del
    // mismo header hace parpadear el fondo — es el defecto que tiene el
    // `focusout` de `header.js`.
    header.addEventListener('focusout', function (event) {
      const next = event.relatedTarget;
      if (next && header.contains(next)) return;
      focusInside = false;
      update();
    });
  }

  bindHeaderHover();

  /*
   * Disparador del mega-menu. `burger-menu.js` agrega `.burger-menu--open` al
   * panel y `overflow-hidden-drawer` al `<body>` en el mismo momento, asi que
   * observar la clase del body alcanza para despertar; la decision la toma
   * `forcedSolid()` leyendo `.burger-menu--open`, que es la senal precisa
   * (`overflow-hidden-drawer` la comparten otros drawers).
   */
  new MutationObserver(function () {
    update();
  }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('shopify:section:load', function () {
    // El editor puede reemplazar el nodo del header, y con el los listeners de
    // hover/foco. `overlayHere()` no necesita invalidarse: lee el DOM cada vez.
    bindHeaderHover();
    schedule();
  });

  // Swiper marca su contenedor con `swiper-initialized`, asi que observar la
  // clase avisa cuando la instancia esta lista —y cuando se recrea— sin poll.
  const demo = document.querySelector('.slider-demo');
  if (demo) {
    new MutationObserver(schedule).observe(demo, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  window.addEventListener('resize', publishChromeHeight, { passive: true });
  document.addEventListener('shopify:section:load', publishChromeHeight);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(publishChromeHeight).catch(function () {});
  }

  publishChromeHeight();
  /*
   * Sincrono, no `schedule()`: este archivo carga **ultimo** (despues de
   * `slider-demo.js`, que ya llamo a `changeNavStyle()` desde `initSwiper()`),
   * asi que con un rAF el primer frame se pintaba con el header solido encima
   * del slideshow — parte del sintoma que se esta arreglando.
   */
  update();
})();
